import { computed, DestroyRef, inject, Injectable, signal, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, fromEvent, merge } from 'rxjs';

/**
 * Breakpoint thresholds. Kept in sync with
 * `src/styles/abstracts/_variables.scss`.
 */
export const BREAKPOINTS = {
  sm: 576,
  md: 768,
  lg: 1120,
  xl: 1280,
  '2xl': 1536,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;
export type Orientation = 'portrait' | 'landscape';

/**
 * Viewport Service
 *
 * Single source of truth for screen-size and orientation in Angular-signal form.
 * Replaces ad-hoc `BreakpointObserver` usage. One resize listener feeds signals
 * the rest of the app can subscribe to via `effect()` or template interpolation.
 *
 * @example
 * ```typescript
 * private viewport = inject(ViewportService);
 *
 * // Use in template
 * @if (viewport.isMobile()) { ... }
 *
 * // Use in effect
 * effect(() => {
 *   if (this.viewport.isXs()) this.drawer.close();
 * });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class ViewportService {
  private destroyRef = inject(DestroyRef);

  private readonly _width = signal<number>(this.getInitialWidth());
  private readonly _height = signal<number>(this.getInitialHeight());
  private readonly _orientation = signal<Orientation>(this.getInitialOrientation());

  readonly width: Signal<number> = this._width.asReadonly();
  readonly height: Signal<number> = this._height.asReadonly();
  readonly orientation: Signal<Orientation> = this._orientation.asReadonly();

  readonly isXs = computed(() => this._width() < BREAKPOINTS.sm);
  readonly isSm = computed(() => this._width() >= BREAKPOINTS.sm);
  readonly isMd = computed(() => this._width() >= BREAKPOINTS.md);
  readonly isLg = computed(() => this._width() >= BREAKPOINTS.lg);
  readonly isXl = computed(() => this._width() >= BREAKPOINTS.xl);
  readonly is2xl = computed(() => this._width() >= BREAKPOINTS['2xl']);

  /** < md (768px) — phones */
  readonly isMobile = computed(() => this._width() < BREAKPOINTS.md);
  /** md..lg (768..1120px) — tablets */
  readonly isTablet = computed(
    () => this._width() >= BREAKPOINTS.md && this._width() < BREAKPOINTS.lg
  );
  /** >= lg (1120px) — desktop */
  readonly isDesktop = computed(() => this._width() >= BREAKPOINTS.lg);

  constructor() {
    if (typeof window === 'undefined') return;

    merge(fromEvent(window, 'resize'), fromEvent(window, 'orientationchange'))
      .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.sync());
  }

  /** True when viewport width is >= the given breakpoint. */
  isAtLeast(bp: Breakpoint): boolean {
    return this._width() >= BREAKPOINTS[bp];
  }

  /** True when viewport width is strictly below the given breakpoint. */
  isBelow(bp: Breakpoint): boolean {
    return this._width() < BREAKPOINTS[bp];
  }

  private sync(): void {
    this._width.set(window.innerWidth);
    this._height.set(window.innerHeight);
    this._orientation.set(window.innerWidth >= window.innerHeight ? 'landscape' : 'portrait');
  }

  private getInitialWidth(): number {
    return typeof window !== 'undefined' ? window.innerWidth : 1024;
  }

  private getInitialHeight(): number {
    return typeof window !== 'undefined' ? window.innerHeight : 768;
  }

  private getInitialOrientation(): Orientation {
    if (typeof window === 'undefined') return 'landscape';
    return window.innerWidth >= window.innerHeight ? 'landscape' : 'portrait';
  }
}
